import { PROJ4_DEFINITIONS, ZONES_MERCHICH } from '../utils/constants';

class MerchichConverter {
  constructor() {
    this.cache = new Map();
    this.initialized = false;
  }

  initializeProjections() {
    if (this.initialized) return true;

    if (typeof window !== 'undefined' && window.proj4) {
      // Définir toutes les zones Merchich dans Proj4
      Object.entries(PROJ4_DEFINITIONS).forEach(([code, def]) => {
        window.proj4.defs(code, def);
      });
      // Définir WGS84 si pas déjà fait
      if (!window.proj4.defs['EPSG:4326']) {
        window.proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
      }
      this.initialized = true;
      console.log('✅ Projections Merchich initialisées (4 zones)');
      return true;
    } else {
      console.error('❌ Proj4 non disponible');
      return false;
    }
  }

  // Détection automatique de zone selon latitude
  detectZone(latitude, longitude) {
    if (latitude >= 31.5) return "EPSG:26191";      // Zone 1: Nord
    if (latitude >= 28.0) return "EPSG:26192";      // Zone 2: Sud
    if (latitude >= 24.0) return "EPSG:26194";      // Zone 3: Sahara Nord
    return "EPSG:26195";                            // Zone 4: Sahara Sud
  }

  // Conversion Merchich vers WGS84
  merchichToWGS84(x, y, zone) {
    if (!this.initializeProjections()) {
      console.error('Proj4 non disponible');
      return null;
    }

    // Vérifier le cache
    const cacheKey = `${x},${y},${zone}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const result = window.proj4(zone, "EPSG:4326", [parseFloat(x), parseFloat(y)]);
      const converted = {
        longitude: result[0],
        latitude: result[1],
        zone: zone
      };

      // Mettre en cache (max 1000 entrées)
      if (this.cache.size > 1000) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }
      this.cache.set(cacheKey, converted);

      return converted;
    } catch (error) {
      console.error(`Erreur conversion Merchich→WGS84: ${error.message}`);
      return null;
    }
  }

  // Conversion WGS84 vers Merchich
  wgs84ToMerchich(latitude, longitude, zone = null) {
    if (!this.initializeProjections()) {
      console.error('Proj4 non disponible');
      return null;
    }

    // Détection auto si zone non spécifiée
    if (!zone) {
      zone = this.detectZone(latitude, longitude);
    }

    try {
      const result = window.proj4("EPSG:4326", zone, [longitude, latitude]);
      return {
        x: result[0],
        y: result[1],
        zone: zone
      };
    } catch (error) {
      console.error(`Erreur conversion WGS84→Merchich: ${error.message}`);
      return null;
    }
  }

  // Validation coordonnées selon zone
  validateCoordinates(x, y, zone) {
    const zoneInfo = ZONES_MERCHICH.find(z => z.id === zone);
    if (!zoneInfo || !zoneInfo.bounds) return { valid: false, message: 'Zone invalide' };

    const numX = parseFloat(x);
    const numY = parseFloat(y);

    if (isNaN(numX) || isNaN(numY)) {
      return { valid: false, message: 'Coordonnées non numériques' };
    }

    const { bounds } = zoneInfo;
    const isValid = numX >= bounds.xMin && numX <= bounds.xMax &&
      numY >= bounds.yMin && numY <= bounds.yMax;

    if (!isValid) {
      return {
        valid: false,
        message: `Coordonnées hors limites pour ${zoneInfo.name}`
      };
    }

    return { valid: true, message: '' };
  }

  // Conversion polygone complet
  convertPolygon(coordinates, fromZone) {
    return coordinates.map(coord => {
      const wgs84 = this.merchichToWGS84(coord.x, coord.y, fromZone);
      return wgs84 ? {
        ...coord,
        lat: wgs84.latitude,
        lng: wgs84.longitude
      } : null;
    }).filter(coord => coord !== null);
  }

  // Calcul centre polygone
  calculatePolygonCenter(coordinates, zone) {
    if (!coordinates || coordinates.length === 0) return null;

    const convertedCoords = this.convertPolygon(coordinates, zone);
    if (convertedCoords.length === 0) return null;

    const totalLat = convertedCoords.reduce((sum, coord) => sum + coord.lat, 0);
    const totalLng = convertedCoords.reduce((sum, coord) => sum + coord.lng, 0);

    return {
      lat: totalLat / convertedCoords.length,
      lng: totalLng / convertedCoords.length
    };
  }

  // Obtenir le premier point converti (pour les marqueurs)
  getFirstPointWGS84(coordinates, zone) {
    if (!coordinates || coordinates.length === 0) return null;

    const firstCoord = coordinates[0];
    const wgs84 = this.merchichToWGS84(firstCoord.x, firstCoord.y, zone);

    if (wgs84) {
      return {
        ...firstCoord,
        lat: wgs84.latitude,
        lng: wgs84.longitude
      };
    }
    return null;
  }

  // Nettoyer le cache
  clearCache() {
    this.cache.clear();
  }
}

// Instance singleton
export const merchichConverter = new MerchichConverter();

// Fonction d'initialisation
export const initializeProj4 = () => {
  return merchichConverter.initializeProjections();
};

export { MerchichConverter };
